/**
 * Estimación de viviendas habitadas por manzana.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * POR QUÉ HACE FALTA ESTIMARLAS
 * ──────────────────────────────────────────────────────────────────────────
 *
 * §5.3.2 fija el salto sistemático como `k = viviendas estimadas en el
 * conglomerado ÷ entrevistas asignadas`. El número de viviendas es un dato
 * CENSAL y la cartografía que tenemos es ELECTORAL: el BGDE del INE publica
 * el polígono de cada manzana pero no cuántas viviendas hay dentro.
 *
 * Sin ese número no hay k, y sin k el encuestador no tiene regla de recorrido
 * dentro de la manzana —que es justo lo que convierte el trabajo de campo en
 * una muestra y no en una selección a conveniencia del entrevistador—.
 *
 * Esto estima las viviendas a partir del ÁREA de la manzana. Es una
 * aproximación declarada, no un dato: en cuanto se cargue una capa con
 * `TVIVHAB` del censo del INEGI, el valor real la sustituye sin tocar código.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * DE DÓNDE SALEN LAS DENSIDADES
 * ──────────────────────────────────────────────────────────────────────────
 *
 * Se midió el tamaño real de las 3,256 manzanas de Tecate, por estrato
 * (mediana en hectáreas):
 *
 *   A urbana consolidada      0.87      C urbana periférica    0.73
 *   B urbana pendular         0.61      D rural / semiurbana   1.46
 *   E mixta periférica        0.69
 *
 * Sobre ese tamaño se aplica una densidad por estrato. El orden de magnitud
 * viene de la traza mexicana típica: en colonia popular consolidada el lote
 * ronda los 120–200 m² y cerca del 70% de la manzana es lote —el resto es
 * banqueta y servidumbre—, lo que da unas 35–45 viviendas por hectárea. Los
 * estratos periféricos y el rural bajan por lote más grande, baldíos y
 * dispersión.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * SE FALLA A PROPÓSITO HACIA ABAJO
 * ──────────────────────────────────────────────────────────────────────────
 *
 * Las densidades son deliberadamente conservadoras, y la dirección del error
 * importa más que su tamaño:
 *
 *   · Si se SUBESTIMAN las viviendas, k sale pequeño y el encuestador recorre
 *     una fracción menor de la manzana de la que podría. Completa su cuota.
 *   · Si se SOBREESTIMAN, k sale grande, el encuestador agota la manzana
 *     antes de llenar sus ~7 entrevistas y tiene que improvisar: o se salta a
 *     otra manzana sin regla, o rebaja el salto sobre la marcha. Las dos
 *     cosas rompen la probabilidad de selección, y ninguna queda registrada.
 *
 * Por eso se prefiere equivocarse por defecto.
 */
/**
 * Viviendas particulares habitadas por hectárea BRUTA de manzana —incluyendo
 * la superficie que no es lote—.
 */
export const DWELLING_DENSITY_PER_HECTARE = {
    /** Traza consolidada, lote chico, ocupación alta. */
    A: 32,
    /** Consolidada pero con más baldío que A. */
    B: 28,
    /** Periferia urbana: lote más grande, menos ocupación. */
    C: 24,
    /** Rural y semiurbano: vivienda dispersa en manzanas grandes. */
    D: 6,
    /** Asentamiento reciente en consolidación: muchos lotes aún vacíos. */
    E: 18,
};
/** Hectáreas por km². */
const HECTAREAS_POR_KM2 = 100;
/**
 * Viviendas habitadas estimadas en una manzana.
 *
 * Devuelve `null` cuando no hay área con la que estimar: es preferible a
 * devolver 0, que el sorteo leería como manzana deshabitada y que al sumarse
 * daría un k sin sentido.
 */
export function estimateDwellingsFromArea(areaKm2, stratum) {
    if (areaKm2 === null || areaKm2 === undefined)
        return null;
    if (!Number.isFinite(areaKm2) || areaKm2 <= 0)
        return null;
    const hectareas = areaKm2 * HECTAREAS_POR_KM2;
    const estimadas = Math.round(hectareas * DWELLING_DENSITY_PER_HECTARE[stratum]);
    // Una manzana con geometría tiene al menos una vivienda; cero la sacaría
    // del sorteo de §5.3.1 como si estuviera deshabitada.
    return Math.max(1, estimadas);
}
export const DWELLINGS_SOURCE_LABELS = {
    censo: 'Censo INEGI',
    manual: 'Capturado en el panel',
    'estimado-area': 'Estimado por superficie',
};
/* -------------------------------------------------------------------------- */
/*                   Suficiencia de viviendas por conglomerado                */
/* -------------------------------------------------------------------------- */
/**
 * Viviendas que hacen falta por cada entrevista lograda.
 *
 * NO sale del documento: §5 fija 3 manzanas por conglomerado y da por hecho
 * que alcanzan. Con la cartografía cargada se puede comprobar, y en las zonas
 * de baja densidad no alcanzan.
 *
 * El 2 es un piso deliberadamente blando. No todas las viviendas de una
 * manzana producen entrevista: hay deshabitadas, nadie en casa tras los tres
 * intentos de §5.3, rechazos y hogares no elegibles. §5.3 pide registrar esas
 * disposiciones justamente para calcular la tasa de respuesta AAPOR RR3, que
 * en levantamiento domiciliario rara vez pasa del 60%. Con menos de dos
 * viviendas por entrevista el encuestador agota su conglomerado sin llenar la
 * cuota, y lo que hace entonces —seguir a la manzana de al lado sin regla— es
 * lo que convierte una muestra probabilística en una de conveniencia.
 */
export const MIN_DWELLINGS_PER_INTERVIEW = 2;
/**
 * ¿Alcanzan las viviendas del conglomerado para su cuota?
 *
 * Se separa «imposible» de «justo» porque piden decisiones distintas: lo
 * imposible exige sortear manzanas adicionales antes de salir a campo; lo
 * justo es un riesgo que basta con vigilar en el monitoreo diario.
 */
export function assessDwellingCoverage(dwellings, targetInterviews) {
    if (dwellings === null || dwellings === undefined || targetInterviews <= 0)
        return null;
    const ratio = dwellings / targetInterviews;
    return {
        dwellings,
        targetInterviews,
        ratio,
        impossible: ratio < 1,
        tight: ratio >= 1 && ratio < MIN_DWELLINGS_PER_INTERVIEW,
    };
}
//# sourceMappingURL=dwellings.js.map